import { chatConversationsQueryKey, useChatStore, useChatUserId } from "@/features/chat";
import {
  ShareLinkRow,
  useChatShareDetail,
  useContinueChatShare,
  useMarkShareSeen,
  useMyShares,
  useReceivedShares,
  useRevokeShare,
  useUnseenSharesCount,
} from "@/features/share";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  SegmentedControl,
  SharedConversationTurns,
  Skeleton,
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
  SurfaceList,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Link2, Loader2, MessageSquarePlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Condivisioni — conversations and answers sent to a link, a colleague, an
 * email or WhatsApp. Its own rail entry (``shares`` tab, placement "spazi");
 * Ando Connect's Spaces are a different thing and live under ``/connect``.
 *
 * Two lanes:
 *   - **Inviate**  → ``GET /chat/shares/mine`` (revoke, copy, open)
 *   - **Ricevute** → ``GET /chat/shares/received`` (in-app org-member shares)
 *
 * URL contract is the shared workspace one: ``?sub=received`` picks the lane,
 * ``?focus=<share_id>`` opens the drawer on that share — exactly what the
 * recipient's notification links to. Arriving without ``sub`` while unseen
 * received shares exist lands on Ricevute. Opening a received share stamps it
 * seen (``POST /chat/shares/{id}/seen``), which refreshes the rail badge.
 */
export const Route = createFileRoute("/_authed/workspace/shares")({
  validateSearch: validateWorkspaceSearch,
  component: SharesHub,
});

type Lane = "mine" | "received";

function laneFromSub(sub: string | undefined): Lane | null {
  return sub === "received" || sub === "mine" ? sub : null;
}

function SharesHub() {
  const { i18n } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const unseen = useUnseenSharesCount();
  const [lane, setLane] = useState<Lane>(() => laneFromSub(search.sub) ?? "mine");
  const openShareId = search.focus ?? null;

  // No lane in the URL + something new for me → land on Ricevute, once.
  const autoLaned = useRef(laneFromSub(search.sub) !== null);
  useEffect(() => {
    if (autoLaned.current || unseen === 0) return;
    autoLaned.current = true;
    setLane("received");
  }, [unseen]);

  const mineQuery = useMyShares({ enabled: lane === "mine" });
  const receivedQuery = useReceivedShares({ enabled: lane === "received" });
  const revoke = useRevokeShare();

  const active = lane === "mine" ? mineQuery : receivedQuery;
  const items = active.data?.items ?? [];
  const total = active.data?.total ?? 0;

  const setFocus = (shareId: string | null) =>
    void navigate({
      search: (prev) => ({ ...prev, sub: lane, focus: shareId ?? undefined }),
      replace: true,
    });

  const meta = active.isLoading ? (
    <span className="text-ink-3">
      <Trans>Caricamento…</Trans>
    </span>
  ) : (
    <>
      <strong className="tabular-nums">{total}</strong>{" "}
      {total > 0 ? (
        lane === "mine" ? (
          <Trans>condivisioni inviate · puoi revocarle in qualsiasi momento</Trans>
        ) : (
          <Trans>
            condivisioni ricevute · aprile e continua la conversazione nel tuo workspace
          </Trans>
        )
      ) : lane === "mine" ? (
        <Trans>inviate</Trans>
      ) : (
        <Trans>ricevute</Trans>
      )}
    </>
  );

  return (
    <SurfaceLayout
      header={<SurfaceHeader title={i18n._(msg`Conversazioni e risposte`)} meta={meta} />}
      toolbar={
        <div className="px-8 pt-4 pb-2">
          <SegmentedControl<Lane>
            options={[
              { value: "mine", label: i18n._(msg`Inviate`) },
              { value: "received", label: i18n._(msg`Ricevute`), badge: unseen },
            ]}
            value={lane}
            onChange={(next) => {
              setLane(next);
              void navigate({
                search: (prev) => ({ ...prev, sub: next, focus: undefined }),
                replace: true,
              });
            }}
            ariaLabel={i18n._(msg`Inviate o ricevute`)}
          />
        </div>
      }
    >
      {active.isError ? (
        <SurfaceEmptyState
          icon={<Link2 size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Impossibile caricare le condivisioni`)}
          description={i18n._(msg`Riprova fra qualche secondo.`)}
          action={
            <button
              type="button"
              onClick={() => void active.refetch()}
              className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-white text-xs"
            >
              <Trans>Riprova</Trans>
            </button>
          }
        />
      ) : !active.isLoading && items.length === 0 ? (
        <SurfaceEmptyState
          icon={<Link2 size={18} strokeWidth={1.75} />}
          title={
            lane === "mine"
              ? i18n._(msg`Ancora nessuna condivisione`)
              : i18n._(msg`Nessuna condivisione ricevuta`)
          }
          description={
            lane === "mine"
              ? i18n._(msg`Usa Condividi su una risposta di Ando o dal menu ⋯ di una chat.`)
              : i18n._(
                  msg`Le risposte e le chat che i tuoi colleghi ti inoltreranno appariranno qui.`,
                )
          }
        />
      ) : (
        <SurfaceList
          flat
          aria-label={lane === "mine" ? i18n._(msg`Inviate`) : i18n._(msg`Ricevute`)}
        >
          {items.map((s) => (
            <ShareLinkRow
              key={s.id}
              share={s}
              canRevoke={lane === "mine"}
              unseen={lane === "received" && s.recipient_seen_at === null}
              onRevoke={(id) => {
                void revoke.mutateAsync(id);
              }}
              onOpen={(share) => setFocus(share.id)}
            />
          ))}
        </SurfaceList>
      )}

      <ShareDetailDrawer
        shareId={openShareId}
        canContinue={lane === "received"}
        onOpenChange={(open) => (open ? undefined : setFocus(null))}
      />
    </SurfaceLayout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Detail drawer — frozen turns + "Continua da qui"
// ──────────────────────────────────────────────────────────────────────

function ShareDetailDrawer({
  shareId,
  canContinue,
  onOpenChange,
}: {
  shareId: string | null;
  canContinue: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { i18n, t } = useLingui();
  const detail = useChatShareDetail(shareId);
  const continueShare = useContinueChatShare();
  const markSeen = useMarkShareSeen();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userId = useChatUserId();
  const selectConversation = useChatStore((s) => s.selectConversation);

  // First open by the recipient → stamp it (idempotent BE-side, once per id here).
  const stampedRef = useRef<string | null>(null);
  useEffect(() => {
    const d = detail.data;
    if (!canContinue || !d || d.recipient_seen_at !== null || stampedRef.current === d.id) return;
    stampedRef.current = d.id;
    markSeen.mutate(d.id);
  }, [canContinue, detail.data, markSeen]);

  const title = detail.data?.title || detail.data?.query_text || t`Condivisione`;
  const sharedBy = detail.data?.owner_name?.trim();
  const when = detail.data ? formatRelativeTime(detail.data.created_at) : null;

  const handleContinue = () => {
    if (!shareId) return;
    continueShare.mutate(shareId, {
      onSuccess: async (res) => {
        // The new thread exists BE-side; refresh the sidebar list, then land
        // the user on it — the next message continues from the copied turns.
        await queryClient.invalidateQueries({ queryKey: chatConversationsQueryKey(userId) });
        selectConversation(res.conversation_id);
        onOpenChange(false);
        toast.success(t`Conversazione copiata nel tuo workspace`);
        void navigate({ to: "/" });
      },
      onError: (err) => {
        toast.error(t`Impossibile continuare la conversazione`, {
          description: err.message || undefined,
        });
      },
    });
  };

  return (
    <Drawer open={shareId !== null} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        ariaTitle={i18n._(msg`Dettaglio condivisione`)}
        className="flex w-[min(640px,95vw)] flex-col sm:max-w-none"
      >
        <DrawerHeader>
          <DrawerTitle className="pr-8">{title}</DrawerTitle>
          <DrawerDescription asChild>
            <div className="flex flex-col gap-1">
              {detail.data?.summary ? (
                <p className="text-ink-2 text-xs">{detail.data.summary}</p>
              ) : null}
              <p className="text-2xs text-ink-3">
                {sharedBy ? <Trans>Condiviso da {sharedBy}</Trans> : <Trans>Condivisione</Trans>}
                {when ? ` · ${when}` : ""}
                {detail.data ? (
                  <>
                    {" · "}
                    <Plural value={detail.data.turn_count} one="# messaggio" other="# messaggi" />
                  </>
                ) : null}
              </p>
            </div>
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody className="min-h-0 flex-1 overflow-y-auto px-6">
          {detail.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-8 w-1/2 self-end" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : detail.isError ? (
            <p role="alert" className="text-rose-600 text-xs">
              <Trans>Questa condivisione non è più disponibile.</Trans>
            </p>
          ) : detail.data ? (
            <SharedConversationTurns turns={detail.data.turns} className="pb-6" />
          ) : null}
        </DrawerBody>

        {canContinue ? (
          <DrawerFooter className="border-hairline border-t">
            <p className="mr-auto text-2xs text-ink-3">
              <Trans>Ne ricevi una copia privata: il thread originale non cambia.</Trans>
            </p>
            <Button
              variant="primary"
              size="md"
              disabled={!detail.data || continueShare.isPending}
              onClick={handleContinue}
            >
              {continueShare.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <MessageSquarePlus aria-hidden />
              )}
              {continueShare.isPending ? t`Copio…` : t`Continua da qui`}
            </Button>
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
